import React, { useEffect, useMemo, useState } from 'react'
import { Form, Grid } from 'semantic-ui-react'
import ReservationSummary from '../../components/ReservationSummary'
import Panel from '../../components/shared/Panel'
import { useTextResources } from '../../language'
import moment from 'moment'
import CurrencyInput from '../../components/shared/CurrencyInput'
import PricingTypesDropDown from '../../components/shared/PricingTypesDropdown'
import ProductsDropDown from '../../components/shared/ProductsDropDown'
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown'
import { Currencies } from '../../components/shared/currencies'
import _ from 'lodash'
import RoomsDropDown from '../../components/shared/RoomsDropDown'
import { CalculateTotalAmountCurrentReservation } from '../../api/services/pms'
import { onError, onWarning } from '../../components/shared/messages'
import formatCurrency from 'format-currency'
import PropTypes from 'prop-types'
import { Guests, Reservations } from '../../api/services/next'
import { ReservationMenu } from './menu'
import { GuestPanel } from './panels/guestPanel'
import { useLoader } from '../../components/shared/utils'
import { DetailsPanel } from './panels/detailsPanel'
import ReservationContext from "./context";
import { CustomerPanel } from './panels/customerPanel'

const ReservationPage = ({ reservationId, reservation: initialReservation }) => {
    
    const text = useTextResources()
    const loader = useLoader()
    const [reservation, setReservation] = useState(initialReservation || {})
    const [guest, setGuest] = useState({})
    const [isRecalculating, setIsReCalculating] = useState(false)
    

    const currencySymbol = Currencies[reservation.CurrencyId ? reservation.CurrencyId : 1].symbol

    useEffect(() => {
        if (reservationId) {
            (async () => {
                try {
                    loader(true)
                    const reservation = await Reservations.GetReservation(reservationId, {
                        includes: []
                    })
                    setReservation(reservation)
                } catch (error) {
                    onError(error)
                } finally {
                    loader(false)
                }
            })()
        }
    }, [reservationId])

    useEffect(() => {
        setReservation(initialReservation || {})
    }, [initialReservation])

    useEffect(()=>{
        if (reservation.GuestId) {
            (async () => {
                try {
                    loader(true)
                    const guest = await Guests.GetGuest(reservation.GuestId)
                    setGuest(guest)
                } catch (error) {
                    onError(error)
                } finally {
                    loader(false)
                }
            })()
        }
    }, [reservation.GuestId])

    const ProductsSpec = React.useMemo(() => {
        return _.isEmpty(reservation) ? null : {
            HotelID: reservation.HotelId,
            IncludeDayPricing: true,
            IsDayUse: moment(reservation.CheckInDate).isSame(reservation.CheckOutDate, "day"),
            MaxAdults: reservation.Adults,
            MaxChildren: reservation.Children,
            MaxInfants: reservation.Infants,
            RoomTypeId: reservation.UpgradedFromRoomTypeID || reservation.RoomTypeId,
            StartDate: moment(reservation.CheckInDate),
            EndDate: moment(reservation.CheckOutDate),
            AvoidAllRestriction: true,
            IsNewAlgo: true,
            IsEnforceLeadDay: false
        }
    }, [reservation.HotelId, reservation.Adults, reservation.Children, reservation.Infants, reservation.RoomTypeId, reservation.UpgradedFromRoomTypeID, reservation.CheckInDate, reservation.CheckOutDate])


    const RoomTypesSpec = React.useMemo(() => {
        return _.isEmpty(reservation) ? null : {
            HotelID: reservation.HotelId
        }
    }, [reservation.HotelId])

    const currencyFormat = currencyID => {
        return {
            format: '%v%s',
            symbol: Currencies[currencyID]?.symbol,
            locale: document.documentElement.lang || "he-IL"
        }
    }

    const productLabel = React.useCallback(item => {
        return formatCurrency(item.TotalAmountAfterTax, currencyFormat(item.CurrencyId))
    }, [])

    const RoomsSpec = React.useMemo(() => {
        return _.isEmpty(reservation) ? null : {
            HotelID: reservation.HotelId,
            RoomTypeIDs: reservation.RoomTypeId,
            FromAdultsMaxPax: reservation.Adults,
            FromChildrenMaxPax: reservation.Children,
            FromInfantsMaxPax: reservation.Infants,
            AvailableFrom: moment(reservation.CheckInDate),
            AvailableTo: moment(reservation.CheckOutDate)
        }
    }, [reservation.HotelId, reservation.RoomTypeId, reservation.Adults, reservation.Children, reservation.Infants, reservation.CheckInDate, reservation.CheckOutDate])

    

    const setProduct = val => {
        setReservation({
            ...reservation,
            ProductID: val,
            // Remove any daily values to rebuild by the server when changing product
            ReservationDailyAccommodations: [],
            ReservationDailyAddons: []
        })
    }
    const setProductItem = product => {
        const currencyID = product ? product.Currency ? product.Currency.Id : product.CurrencyId : null
        setReservation({ ...reservation, CurrencyId: currencyID })
    }

    

    
    const setPricingType = val => {
        setReservation({ ...reservation, ReservationPricingTypeID: val })
    }

    const setRoomType = val => {
        setReservation({ ...reservation, RoomTypeId: val, ProductID: null, UpgradedFromRoomTypeID: null })
    }

    const setRoom = val => {
        setReservation(prev => {
            if (prev.ReservationDailyAccommodations) {
                _.forEach(prev.ReservationDailyAccommodations, accomodation => {
                    accomodation.RoomId = val
                })
            }
            return { ...prev, RoomId: val }
        })
    }

    // This is a simple check if any changes occured during the price recalculation that should cancel the price update
    const calc = useMemo(() => _.debounce(async (reservation, manual) => {
        try {
            setIsReCalculating(true)
            const updated = await CalculateTotalAmountCurrentReservation({
                Reservation: {
                    ...reservation,
                    IsDayUse: moment(reservation.CheckInDate).isSame(reservation.CheckOutDate, "day")
                },
                IsReCalculateTax: true,
                IncludeCommissionCalculation: reservation.Id ? true : false,
                DigitsPercision: 8
            })

            if (manual) {
                if (reservation.IsOverriddenTotalAmountAfterTax) {
                    setReservation(prev => {
                        // If ProductID changed during price calculation than skip this update
                        return prev.ProductID === reservation.ProductID ?
                            { ...prev, TotalAmountProductCurrentBeforeTax: updated.TotalAmountProductCurrentBeforeTax } : prev
                    })
                } else {
                    setReservation(prev => {
                        // If ProductID changed during price calculation than skip this update
                        return prev.ProductID === reservation.ProductID ?
                            { ...prev, TotalAmountProductCurrentAfterTax: updated.TotalAmountProductCurrentAfterTax } : prev
                    })
                }
            } else {
                setReservation(prev => {
                    // If ProductID changed during price calculation than skip this update
                    return prev.ProductID === reservation.ProductID ?
                        {
                            ...prev,
                            TotalAmountProductCurrentBeforeTax: updated.TotalAmountProductCurrentBeforeTax,
                            TotalAmountProductCurrentAfterTax: updated.TotalAmountProductCurrentAfterTax
                        } : prev
                })
            }
        } catch (error) {
            console.error(error)
            error.status === 400 ? onWarning(error) : onError(error)
        } finally {
            setIsReCalculating(false)
        }
    }, 500), [])

    const setTotalPrice = (val, isAfterTax) => {
        const value = Number(val)
        const updated = {
            ...reservation,
            TotalAmountProductCurrentBeforeTax: isAfterTax ? reservation.TotalAmountProductCurrentBeforeTax : value,
            TotalAmountProductCurrentAfterTax: isAfterTax ? value : reservation.TotalAmountProductCurrentAfterTax,
            OverriddenTotalAmount: value,
            IsOverriddenTotalAmountAfterTax: isAfterTax,
            OverrideProductPrice: true
        }
        setReservation(updated)
        calc(updated, true)
    }

    

    

    return <ReservationContext.Provider value={{ reservation, setReservation, guest, setGuest }}><div style={{ flex: 1, display: "flex", flexDirection: "row" }}>
        <div style={{marginRight: 8}}>
            <ReservationMenu />
        </div>
        <div style={{ flex: 1, display: "flex", overflow: "auto"}}>
            <Grid style={{ flex: 1 }}>
                <Grid.Row>
                    <Grid.Column width={12}>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column width="6" stretched>
                                    <GuestPanel/>
                                </Grid.Column>
                                <Grid.Column width="6" stretched>
                                    <DetailsPanel />
                                </Grid.Column>
                                <Grid.Column width="4" stretched>
                                    <CustomerPanel/>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width="6">
                                    <Panel header={text("RoomAndPrice")}>
                                        <Form>
                                            <Form.Group unstackable>
                                                <Form.Field width="10" label={text("RoomType")} required control={RoomTypesDropDown}
                                                    spec={RoomTypesSpec}
                                                    value={reservation.RoomTypeId}
                                                    onChange={setRoomType}
                                                    onValueNotFound={() => setRoomType(null)} />
                                                <Form.Field width="6" label={text("Room")} control={RoomsDropDown}
                                                    spec={RoomsSpec}
                                                    clearable
                                                    value={reservation.RoomId}
                                                    onChange={setRoom}
                                                    onValueNotFound={() => setRoom(null)}
                                                />
                                            </Form.Group>
                                            <Form.Field control={ProductsDropDown} required label={text("Product")}
                                                spec={ProductsSpec}
                                                value={reservation.ProductID}
                                                descriptionField="RateName"
                                                labelField={productLabel}
                                                onChange={setProduct}
                                                onChangeItem={setProductItem}
                                                onValueNotFound={() => setProduct(null)} />
                                            <Form.Field control={PricingTypesDropDown} label={text("ReservationPricingType_ComboTitle")}
                                                search={false}
                                                value={reservation.ReservationPricingTypeID}
                                                onChange={setPricingType}
                                                onValueNotFound={() => setPricingType(null)}
                                            />
                                            <Form.Group widths="2">
                                                <Form.Field>
                                                    <label >{text("TotalPriceNotIncTax")}</label>
                                                    <CurrencyInput loading={isRecalculating} labelPosition='right' currency={currencySymbol} value={reservation.TotalAmountProductCurrentBeforeTax} onChange={value => setTotalPrice(value, false)} />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label >{text("TotalPriceIncTax")}</label>
                                                    <CurrencyInput loading={isRecalculating} labelPosition='right' currency={currencySymbol} value={reservation.TotalAmountProductCurrentAfterTax} onChange={value => setTotalPrice(value, true)} />
                                                </Form.Field>
                                            </Form.Group>
                                            <Form.Group widths="2">
                                                <Form.Field>
                                                    <label >{text("AveragePricePerNightNotIncTax")}</label>
                                                    <CurrencyInput loading={isRecalculating} labelPosition='right' currency={currencySymbol} value={reservation.TotalAmountProductCurrentBeforeTax} onChange={value => setTotalPrice(value, false)} />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label >{text("AveragePricePerNightIncTax")}</label>
                                                    <CurrencyInput loading={isRecalculating} labelPosition='right' currency={currencySymbol} value={reservation.TotalAmountProductCurrentAfterTax} onChange={value => setTotalPrice(value, true)} />
                                                </Form.Field>
                                            </Form.Group>
                                        </Form>
                                    </Panel>
                                </Grid.Column>
                                <Grid.Column width="8">
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Grid.Column>
                    <Grid.Column width={4} ><ReservationSummary reservation={reservation} /></Grid.Column>
                </Grid.Row>
            </Grid>
        </div>
    </div>
    </ReservationContext.Provider> 
}
ReservationPage.propTypes = {
    reservationId: PropTypes.number,
    reservation: PropTypes.object
}
export default ReservationPage